/**
 * GradeAppeals — /grades/appeals
 *
 * MV5 Sprint 2.C — instructor queue for grade dispute review.
 *
 * Left pane: assigned + open appeals (submitted | in_review), newest first.
 * Right pane: selected appeal with evidence + decision affordance.
 *
 * Decision states:
 *   · in_review — opened via "Start review" (POST /grades/appeals/:id/in-review)
 *   · decided_upheld — appeal declined; the original grade remains recorded
 *   · decided_revised — appeal accepted; any grade correction is a separate,
 *     recorded grading action
 *
 * Every decision fires a `grade_appeal_decided` notification back to the
 * learner via the NotificationsService.emit path.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { hasExactReviewerAccess } from '../lib/reviewerAccess';
import { sessionManagerApi } from '../services/apiClient';

interface AppealHistoryEntry {
  id: string;
  event: string;
  actorId: string | null;
  timestamp: string;
  notes: string | null;
}

interface Appeal {
  id: string;
  submissionId: string;
  learnerId: string;
  reasonCode: string;
  evidenceText: string | null;
  evidenceFiles: Array<{
    storageUrl: string;
    fileName: string;
    mimeType: string;
    byteSize: number;
  }>;
  routeToInstructorId: string | null;
  status:
    | 'submitted'
    | 'in_review'
    | 'decided_upheld'
    | 'decided_revised'
    | 'withdrawn';
  decidedAt: string | null;
  decisionNotes: string | null;
  createdAt: string;
  history: AppealHistoryEntry[];
}

const GradeAppeals: React.FC = () => {
  const oidc = useAuth();
  const profile = (oidc.user?.profile ?? {}) as Record<string, any>;
  const isExactReviewer = hasExactReviewerAccess(profile);
  const [appeals, setAppeals] = useState<Appeal[]>([]);
  const [selected, setSelected] = useState<Appeal | null>(null);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);

  const [notes, setNotes] = useState('');
  const [deciding, setDeciding] = useState(false);
  const decidingRef = useRef(false);
  const selectedRef = useRef<Appeal | null>(null);
  const appealsRef = useRef<Appeal[]>([]);
  const listRequest = useRef(0);
  const detailRequest = useRef(0);
  const invalidatePendingRequests = () => {
    ++listRequest.current;
    ++detailRequest.current;
  };

  const clearSelection = () => {
    ++detailRequest.current;
    selectedRef.current = null;
    setSelected(null);
    setNotes('');
    setDetailLoading(false);
  };

  const loadList = async (afterWrite = false) => {
    if (decidingRef.current && !afterWrite) return;
    const request = ++listRequest.current;
    setLoading(true);
    setListError(null);
    try {
      const resp = await sessionManagerApi.get(
        '/grades/appeals?filter=assigned',
      );
      if (request !== listRequest.current) return;
      if (!Array.isArray(resp.data?.appeals)) {
        throw new Error('Invalid appeal queue response');
      }
      const nextAppeals: Appeal[] = resp.data.appeals;
      appealsRef.current = nextAppeals;
      setAppeals(nextAppeals);
      const current = selectedRef.current;
      if (current) {
        const latest = nextAppeals.find((appeal) => appeal.id === current.id);
        if (!latest) {
          clearSelection();
          setNotice(
            'The selected appeal is no longer in this queue. Its unsaved decision draft was cleared.',
          );
        } else if (
          latest.status !== current.status ||
          latest.routeToInstructorId !== current.routeToInstructorId ||
          latest.submissionId !== current.submissionId ||
          latest.learnerId !== current.learnerId ||
          latest.decidedAt !== current.decidedAt ||
          latest.decisionNotes !== current.decisionNotes
        ) {
          clearSelection();
          setError(
            'This appeal changed. Reopen it from the queue before continuing.',
          );
        }
        // List entries are summaries. Do not replace a verified full record
        // (and erase its evidence/history) when the same appeal is still open.
      }
    } catch (err: any) {
      if (request !== listRequest.current) return;
      appealsRef.current = [];
      setAppeals([]);
      clearSelection();
      setError(null);
      setListError(
        err?.response?.status === 403
          ? 'You do not have access to this appeal queue.'
          : "Couldn't load the appeal queue. No appeal count or empty state could be verified.",
      );
    } finally {
      if (request === listRequest.current) setLoading(false);
    }
  };

  useEffect(() => {
    void loadList();
    // Simple polling — Wave 2 wires WS via /ws/grades/appeals.
    const t = setInterval(() => void loadList(), 20_000);
    return () => {
      clearInterval(t);
      invalidatePendingRequests();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAppeal = async (a: Appeal) => {
    const request = ++detailRequest.current;
    selectedRef.current = a;
    setSelected(a);
    setNotes('');
    setDetailLoading(true);
    setError(null);
    setNotice('');
    try {
      const response = await sessionManagerApi.get(`/grades/appeals/${a.id}`);
      if (request !== detailRequest.current) return;
      const appeal: Appeal | undefined = response.data?.appeal;
      if (
        appeal?.id !== a.id ||
        !appealsRef.current.some((item) => item.id === a.id)
      ) {
        throw new Error('The selected appeal is no longer available');
      }
      selectedRef.current = appeal;
      setSelected(appeal);
      setNotes(appeal.decisionNotes ?? '');
    } catch {
      if (request === detailRequest.current) {
        clearSelection();
        setError(
          "Couldn't load the full appeal record. No appeal is selected. Reopen it from the queue to retry.",
        );
      }
    } finally {
      if (request === detailRequest.current) setDetailLoading(false);
    }
  };

  const openAppeal = async (a: Appeal) => {
    if (decidingRef.current || loading || listError) return;
    await loadAppeal(a);
  };

  const startReview = async () => {
    if (
      !selected ||
      decidingRef.current ||
      loading ||
      listError ||
      detailLoading ||
      isExactReviewer
    )
      return;
    decidingRef.current = true;
    setDeciding(true);
    setError(null);
    try {
      await sessionManagerApi.post(`/grades/appeals/${selected.id}/in-review`);
      await loadAppeal(selected);
      await loadList(true);
    } catch (err: any) {
      if ([403, 404].includes(err?.response?.status)) clearSelection();
      setError("Couldn't start this review. Refresh the queue and try again.");
    } finally {
      decidingRef.current = false;
      setDeciding(false);
    }
  };

  const decide = async (outcome: 'upheld' | 'revised') => {
    if (
      !selected ||
      decidingRef.current ||
      loading ||
      listError ||
      detailLoading ||
      isExactReviewer ||
      selected.status !== 'in_review'
    )
      return;
    decidingRef.current = true;
    setDeciding(true);
    setError(null);
    try {
      const response = await sessionManagerApi.post(
        `/grades/appeals/${selected.id}/decision`,
        {
          outcome,
          notes,
        },
      );
      clearSelection();
      setNotice(
        `Decision recorded: ${statusLabel(response.data.appeal.status)}. Any grade correction is a separate, recorded grading action.`,
      );
      await loadList(true);
    } catch (err: any) {
      if ([403, 404].includes(err?.response?.status)) clearSelection();
      setError(
        err?.response?.data?.message || err?.message || 'Decision failed.',
      );
    } finally {
      decidingRef.current = false;
      setDeciding(false);
    }
  };

  const controlsLocked =
    deciding || loading || Boolean(listError) || detailLoading;

  return (
    <div className="appeals-layout">
      <aside style={{ borderRight: '1px solid #e0e0e0', overflowY: 'auto' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #e0e0e0' }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>
            Grade appeals{!loading && !listError ? ` (${appeals.length})` : ''}
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b6b6b' }}>
            {isExactReviewer ? 'Read-only · all open' : 'Assigned + open'}
          </p>
        </div>
        {listError && (
          <div role="alert" style={{ padding: 16, color: '#7f1d1d' }}>
            <p>{listError}</p>
            <button
              type="button"
              disabled={loading || deciding}
              onClick={() => void loadList()}
              style={btn('secondary')}
            >
              Retry appeal queue
            </button>
          </div>
        )}
        {loading && (
          <div role="status" style={{ padding: 16, color: '#6b6b6b' }}>
            {appeals.length === 0 ? 'Loading appeals…' : 'Refreshing appeals…'}
          </div>
        )}
        {!loading && !listError && appeals.length === 0 && (
          <div style={{ padding: 16, color: '#6b6b6b' }}>
            No open appeals. Learners' disputes land here.
          </div>
        )}
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {appeals.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                disabled={deciding || loading}
                onClick={() => void openAppeal(a)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: 12,
                  background: selected?.id === a.id ? '#f3f0ff' : 'transparent',
                  border: 'none',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {a.reasonCode.replace(/_/g, ' ')}
                </div>
                <div style={{ fontSize: 12, color: '#6b6b6b', marginTop: 4 }}>
                  Submission {a.submissionId.slice(0, 8)}… ·{' '}
                  {statusLabel(a.status)}
                </div>
                <div style={{ fontSize: 11, color: '#8a8a8a', marginTop: 4 }}>
                  {new Date(a.createdAt).toLocaleString()}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <main style={{ overflowY: 'auto', padding: 24 }}>
        {notice && <p role="status">{notice}</p>}
        {error && (
          <div
            role="alert"
            style={{
              padding: 12,
              background: '#fef2f2',
              color: '#7f1d1d',
              borderRadius: 6,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}
        {detailLoading ? (
          <p role="status">Loading the full appeal record…</p>
        ) : !selected ? (
          <p style={{ color: '#6b6b6b' }}>
            {listError
              ? 'The appeal queue must be available before a record can be reviewed.'
              : 'Select an appeal on the left to review.'}
          </p>
        ) : (
          <div>
            <h1 style={{ marginTop: 0 }}>Appeal review</h1>
            <dl style={dl()}>
              <dt style={dt()}>Learner ID</dt>
              <dd style={dd()}>{selected.learnerId}</dd>
              <dt style={dt()}>Submission ID</dt>
              <dd style={dd()}>{selected.submissionId}</dd>
              <dt style={dt()}>Reason</dt>
              <dd style={dd()}>{selected.reasonCode}</dd>
              <dt style={dt()}>Status</dt>
              <dd style={dd()}>{statusLabel(selected.status)}</dd>
              <dt style={dt()}>Submitted</dt>
              <dd style={dd()}>
                {new Date(selected.createdAt).toLocaleString()}
              </dd>
            </dl>

            {selected.evidenceText && (
              <section style={{ marginTop: 24 }}>
                <h3>Learner statement</h3>
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    padding: 12,
                    background: '#f9f9f9',
                    borderRadius: 6,
                  }}
                >
                  {selected.evidenceText}
                </div>
              </section>
            )}

            {selected.evidenceFiles.length > 0 && (
              <section style={{ marginTop: 16 }}>
                <h3>Evidence files</h3>
                <ul>
                  {selected.evidenceFiles.map((f, i) => (
                    <li key={i}>
                      <a
                        href={
                          /^https:\/\//i.test(f.storageUrl)
                            ? f.storageUrl
                            : undefined
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {f.fileName}
                      </a>{' '}
                      <span style={{ color: '#6b6b6b' }}>
                        ({f.mimeType} · {(f.byteSize / 1024).toFixed(1)} KB)
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section style={{ marginTop: 24 }}>
              <h3>History</h3>
              <ul style={{ paddingLeft: 20 }}>
                {selected.history.map((h) => (
                  <li key={h.id} style={{ marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{h.event}</span> —{' '}
                    {new Date(h.timestamp).toLocaleString()}
                    {h.notes && (
                      <div style={{ color: '#6b6b6b' }}>{h.notes}</div>
                    )}
                  </li>
                ))}
              </ul>
            </section>

            {isExactReviewer ? (
              <div
                role="status"
                style={{
                  marginTop: 24,
                  padding: 16,
                  background: '#f5f5f5',
                  borderRadius: 6,
                  color: '#4b5563',
                }}
              >
                Reviewer access is read-only. Decisions and status changes are
                available only to the assigned instructor or an administrator.
              </div>
            ) : selected.status === 'submitted' ||
              selected.status === 'in_review' ? (
              <section style={{ marginTop: 24 }}>
                <h3>Decision</h3>
                {selected.status === 'submitted' && (
                  <button
                    type="button"
                    onClick={() => void startReview()}
                    disabled={controlsLocked}
                    style={btn('secondary')}
                  >
                    Start review
                  </button>
                )}
                <textarea
                  aria-label="Decision notes visible to learner"
                  disabled={controlsLocked || selected.status !== 'in_review'}
                  maxLength={10000}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="Decision notes (visible to learner)…"
                  style={{
                    width: '100%',
                    padding: 10,
                    borderRadius: 6,
                    border: '1px solid #d0d0d0',
                    fontFamily: 'inherit',
                  }}
                />
                <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                  <button
                    type="button"
                    onClick={() => void decide('upheld')}
                    disabled={controlsLocked || selected.status !== 'in_review'}
                    style={btn('secondary')}
                  >
                    Uphold original grade
                  </button>
                  <button
                    type="button"
                    onClick={() => void decide('revised')}
                    disabled={controlsLocked || selected.status !== 'in_review'}
                    style={btn('primary')}
                  >
                    Accept appeal
                  </button>
                </div>
              </section>
            ) : (
              <div
                style={{
                  marginTop: 24,
                  padding: 16,
                  background: '#f0fdf4',
                  borderRadius: 6,
                  color: '#166534',
                }}
              >
                Decision recorded: {statusLabel(selected.status)} on{' '}
                {selected.decidedAt
                  ? new Date(selected.decidedAt).toLocaleString()
                  : '—'}
                . Notes: {selected.decisionNotes || '(none)'}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default GradeAppeals;

function statusLabel(s: string): string {
  switch (s) {
    case 'submitted':
      return 'Awaiting review';
    case 'in_review':
      return 'In review';
    case 'decided_upheld':
      return 'Original grade upheld';
    case 'decided_revised':
      return 'Appeal accepted';
    case 'withdrawn':
      return 'Withdrawn';
    default:
      return s;
  }
}

function dl(): React.CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    gap: '4px 12px',
    margin: 0,
  };
}
function dt(): React.CSSProperties {
  return { fontWeight: 600, color: '#6b6b6b' };
}
function dd(): React.CSSProperties {
  return { margin: 0, overflowWrap: 'anywhere' };
}
function btn(kind: 'primary' | 'secondary'): React.CSSProperties {
  return {
    padding: '8px 14px',
    borderRadius: 6,
    border: kind === 'primary' ? 'none' : '1px solid #d0d0d0',
    background: kind === 'primary' ? '#6b4fea' : 'transparent',
    color: kind === 'primary' ? '#fff' : '#1a1a1a',
    fontWeight: 600,
    cursor: 'pointer',
  };
}
